import {
  Autocomplete,
  Checkbox,
  TextField,
  Divider,
  Stack,
  Grid,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Tooltip,
  IconButton,
} from '@material-ui/core'
import { useState, useEffect } from 'react'
import { fetchDiseases } from 'src/api/filters/phenotypes'
import SearchIcon from '@material-ui/icons/Search'
import ClearIcon from '@material-ui/icons/Clear'
import InheritanceFilter from './InheritanceFilter'

// Map of inheritance mode values to display labels
export const INHERITANCE_MODE_LABELS = {
  1: 'Autosomal Dominant',
  2: 'Autosomal Recessive',
  3: 'Digenic Recessive',
  4: 'Digenic Dominant',
  5: 'Somatic',
  6: 'Multifactorial',
  7: 'X-Linked',
  8: 'X-Linked Dominant',
  9: 'X-Linked Recessive',
  10: 'Y-Linked',
  11: 'Unknown',
}

function PhenotypeFilter({
  phenotypes = [],
  inheritanceModes = [],
  setInheritanceModes,
  showInheritance = true,
  onDeleteDisease,
  onClearAll,
  ...props
}) {
  const [input, setInput] = useState('')
  const [diseases, setDiseases] = useState([])
  const [loading, setLoading] = useState(false)
  const [hoveredOption, setHoveredOption] = useState(null)
  const [selectedInheritanceModes, setSelectedInheritanceModes] = useState(inheritanceModes)

  useEffect(() => {
    // Update parent component when inheritance modes change
    if (setInheritanceModes) {
      setInheritanceModes(selectedInheritanceModes)
    }
  }, [selectedInheritanceModes, setInheritanceModes])

  const handleFetchDiseases = async (searchTerm) => {
    setLoading(true)
    try {
      const params = {}
      if (searchTerm) {
        params.search = searchTerm
      }
      if (showInheritance && selectedInheritanceModes.length > 0) {
        params.inheritance_mode__in = selectedInheritanceModes.join(',')
      }

      const results = await fetchDiseases(params)
      setDiseases(results)
    } catch (error) {
      console.error('Error fetching diseases:', error)
      setDiseases([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (input.length >= 2 || (showInheritance && selectedInheritanceModes.length > 0)) {
        handleFetchDiseases(input)
      } else {
        setDiseases([])
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [input, selectedInheritanceModes, showInheritance])

  const getOptionLabel = (option) => {
    if (option === 'select-all-option') return ''
    if (!option) return ''
    return `${option.disease || ''} ${showInheritance && option.inheritance ? `(${option.inheritance || ''})` : ''}${
      option.omim ? ` [OMIM:${option.omim}]` : ''
    }`
  }

  const handleSelectAll = (checked) => {
    if (checked) {
      const newPhenotypes = [...phenotypes]
      diseases.forEach((disease) => {
        if (!phenotypes.some((p) => p?.key === disease?.key)) {
          newPhenotypes.push(disease)
        }
      })
      props.setPhenotypes(newPhenotypes)
    } else {
      const filteredPhenotypes = phenotypes.filter((phenotype) => !diseases.some((d) => d?.key === phenotype?.key))
      props.setPhenotypes(filteredPhenotypes)
    }
  }

  const areAllCurrentResultsSelected = () => {
    return diseases.length > 0 && diseases.every((disease) => phenotypes.some((p) => p?.key === disease?.key))
  }

  const getOptions = () => {
    if (diseases.length === 0) return []
    return ['select-all-option', ...diseases]
  }

  return (
    <Stack direction="column" spacing={2} divider={<Divider />}>
      {phenotypes !== null && props.setPhenotypes && (
        <>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Selected Diseases ({phenotypes.length})
              </Typography>
              {phenotypes.length > 0 && (
                <IconButton
                  size="small"
                  onClick={onClearAll || (() => props.setPhenotypes([]))}
                  title="Clear all phenotypes"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                maxHeight: '200px',
                overflowY: 'auto',
                p: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '4px',
                  '&:hover': {
                    background: '#555',
                  },
                },
              }}
            >
              {phenotypes.length > 0 ? (
                phenotypes.map((phenotype) => {
                  const label = phenotype.disease
                  const truncatedLabel = label.length > 32 ? `${label.slice(0, 29)}...` : label
                  const needsTooltip = label.length > 32

                  return (
                    <Tooltip key={phenotype.key} title={needsTooltip ? label : ''}>
                      <Chip
                        label={truncatedLabel}
                        onDelete={
                          onDeleteDisease
                            ? () => onDeleteDisease(phenotype.key)
                            : () => {
                                const newPhenotypes = phenotypes.filter((p) => p.key !== phenotype.key)
                                props.setPhenotypes(newPhenotypes)
                              }
                        }
                        sx={{
                          maxWidth: '300px',
                          '& .MuiChip-label': {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          },
                          '&:hover': {
                            backgroundColor: 'primary.light',
                          },
                          transition: 'background-color 0.2s',
                        }}
                      />
                    </Tooltip>
                  )
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No diseases selected
                </Typography>
              )}
            </Box>
          </Box>
          <Autocomplete
            multiple
            disableClearable
            options={getOptions()}
            loading={loading}
            disableCloseOnSelect
            filterOptions={(x) => x}
            // Control the input value without clearing it on select or blur.
            inputValue={input}
            onInputChange={(event, newInputValue, reason) => {
              // Prevent clearing the input when an option is selected ("reset") or on blur.
              if (reason === 'reset' || reason === 'blur') return
              setInput(newInputValue)
            }}
            clearOnBlur={false}
            blurOnSelect={false}
            renderInput={(params) => {
              // Remove the default clear icon and add a custom one that only clears input
              const { InputProps, ...rest } = params
              return (
                <Grid sx={{ display: 'flex' }}>
                  <TextField
                    {...rest}
                    label="Search diseases by name, inheritance or OMIM"
                    variant="outlined"
                    placeholder="Type at least 2 characters to search..."
                    InputProps={{
                      ...InputProps,
                      startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                      endAdornment: (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {params.InputProps.endAdornment}
                          {loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {input.length > 0 && (
                            <IconButton
                              size="small"
                              aria-label="Clear search input"
                              onClick={(e) => {
                                e.stopPropagation()
                                setInput('')
                              }}
                              tabIndex={0}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' && input.length === 0 && phenotypes.length > 0) {
                        e.stopPropagation()
                      }
                    }}
                  />
                </Grid>
              )
            }}
            renderOption={(props, option) => (
              <li
                {...props}
                onMouseEnter={() => setHoveredOption(option)}
                onMouseLeave={() => setHoveredOption(null)}
                style={{
                  transition: 'background-color 0.2s',
                }}
              >
                {option === 'select-all-option' ? (
                  <Box
                    sx={{
                      width: '100%',
                      borderBottom: 1,
                      borderColor: 'divider',
                      pb: 1,
                      backgroundColor: hoveredOption === option ? 'action.hover' : 'transparent',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        checked={areAllCurrentResultsSelected()}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        sx={{
                          '&:hover': { color: 'primary.main' },
                        }}
                      />
                      <Typography
                        sx={{
                          fontWeight: 500,
                          color: areAllCurrentResultsSelected() ? 'primary.main' : 'inherit',
                        }}
                      >
                        Select All Results ({diseases.length})
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      backgroundColor: hoveredOption === option ? 'action.hover' : 'transparent',
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        checked={Array.isArray(phenotypes) && phenotypes.some((p) => p?.key === option?.key)}
                        sx={{
                          '&:hover': { color: 'primary.main' },
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: phenotypes.some((p) => p?.key === option?.key) ? 500 : 400,
                            color: phenotypes.some((p) => p?.key === option?.key) ? 'primary.main' : 'inherit',
                          }}
                        >
                          {option?.disease}
                        </Typography>
                        <Box
                          sx={{
                            fontSize: '0.8em',
                            color: 'text.secondary',
                            display: 'flex',
                            gap: 1,
                            alignItems: 'center',
                          }}
                        >
                          {showInheritance && option?.inheritance && (
                            <Chip
                              label={INHERITANCE_MODE_LABELS[option.inheritance] || option.inheritance}
                              size="small"
                              sx={{ height: 20 }}
                            />
                          )}
                          {option?.omim && (
                            <Tooltip title="OMIM Reference">
                              <Chip
                                label={`OMIM: ${option.omim}`}
                                size="small"
                                sx={{ height: 20 }}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  window.open(`https://omim.org/entry/${option.omim}`, '_blank')
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </li>
            )}
            getOptionLabel={getOptionLabel}
            value={phenotypes || []}
            onChange={(_, val) => {
              const filteredVal = val.filter((v) => v !== 'select-all-option')
              props.setPhenotypes(filteredVal)
            }}
            isOptionEqualToValue={(option, value) => {
              if (option === 'select-all-option') return false
              return option?.key === value?.key
            }}
            noOptionsText={
              <Box sx={{ py: 2, textAlign: 'center' }}>
                {input.length < 2 ? (
                  <Typography color="text.secondary">Type at least 2 characters to search</Typography>
                ) : (
                  <Typography color="text.secondary">No results found</Typography>
                )}
              </Box>
            }
            ListboxProps={{
              style: {
                maxHeight: '400px',
              },
            }}
          />
          {showInheritance && (
            <InheritanceFilter selectedModes={selectedInheritanceModes} onChange={setSelectedInheritanceModes} />
          )}
        </>
      )}
    </Stack>
  )
}

export default PhenotypeFilter
